<template>
  <div>
    <md-tabs>
      <md-tab
        @click="tab = 'confirmation'"
        id="tab-confirmation"
        md-label="Transaction Confirmation"
      >
        <div class="transaction-sms-toggle">
          <div class="toggle-content">
            <div>
              <span class="toggle-label">
                {{ $tc("phrases.transactionSmsEnabled") }}
              </span>
              <p class="toggle-description">
                {{ $tc("phrases.transactionSmsEnabledDescription") }}
              </p>
            </div>
            <md-switch
              v-model="transactionSmsEnabled"
              @change="updateTransactionSmsEnabled"
              class="md-primary"
            />
          </div>
        </div>
        <div
          class="md-layout-item notice-message-area"
          style="margin-bottom: 1rem"
        >
          <p style="font-size: 0.9rem; color: #666">
            {{ $tc("phrases.transactionConfirmationTemplatesNotice") }}
          </p>
        </div>
        <div
          v-for="(smsBody, index) in smsBodiesService.confirmationList"
          :key="index"
        >
          <sms-body
            ref="smsBody_confirmation_ref"
            :sms-variable-default-values="smsVariableDefaultValueService.list"
            :sms-body="smsBody"
          />
        </div>
        <div class="md-layout md-alignment-bottom-right">
          <md-button
            class="md-primary md-dense md-raised"
            @click="updateSmsBodies()"
          >
            Save
          </md-button>
        </div>
      </md-tab>
      <md-tab @click="tab = 'reminder'" id="tab-reminder" md-label="Reminder">
        <sms-appliance-remind-rate />
        <widget title="SMS Reminder Templates" class="reminder-templates">
          <div
            v-for="(smsBody, index) in smsBodiesService.reminderList"
            :key="index"
          >
            <sms-body
              ref="smsBody_reminder_ref"
              :sms-variable-default-values="smsVariableDefaultValueService.list"
              :sms-body="smsBody"
            />
          </div>
          <div class="md-layout md-alignment-bottom-right">
            <md-button
              class="md-primary md-dense md-raised"
              @click="updateSmsBodies()"
            >
              Save
            </md-button>
          </div>
        </widget>
      </md-tab>
      <md-tab
        @click="tab = 'resend-information'"
        id="tab-resend-information"
        md-label="Resend Last Transaction"
      >
        <div class="md-layout md-gutter md-size-100">
          <div class="md-layout-item notice-message-area">
            <p style="font-size: large; font-weight: 500">
              {{ $tc("words.notice") }} !
            </p>
            {{ $tc("phrases.thisKeyWillUseIn") }}
          </div>
          <div class="md-layout-item md-size-100">
            <md-field
              :class="{
                'md-invalid': errors.has('resend_information_key'),
              }"
            >
              <label for="resend_information_key">
                Resend Last Transaction Information Key
              </label>
              <md-input
                v-model="
                  smsResendInformationKeyService.smsResendInformationKey.key
                "
                id="resend_information_key"
                name="resend_information_key"
                v-validate="'required'"
              ></md-input>
              <span class="md-error">
                {{ errors.first("resend_information_key") }}
              </span>
            </md-field>
          </div>
          <div class="md-layout-item md-size-100">
            <div
              v-for="(smsBody, index) in smsBodiesService.resendInformationList"
              :key="index"
            >
              <sms-body
                ref="smsBody_resend_ref"
                :sms-variable-default-values="
                  smsVariableDefaultValueService.list
                "
                :sms-body="smsBody"
              />
            </div>
          </div>
        </div>
        <div class="md-layout md-alignment-bottom-right">
          <md-button
            class="md-primary md-dense md-raised"
            @click="updateSmsBodies()"
          >
            Save
          </md-button>
        </div>
      </md-tab>
      <div class="md-layout md-alignment-bottom-right">
        <md-button
          class="md-primary md-dense md-raised"
          @click="updateSmsBodies()"
        >
          Save
        </md-button>
      </div>
    </md-tabs>
    <md-progress-bar v-if="progress" md-mode="indeterminate"></md-progress-bar>
  </div>
</template>

<script>
import SmsApplianceRemindRate from "./SmsApplianceRemindRate.vue"
import SmsBody from "./SmsBody.vue"

import { notify } from "@/mixins/notify.js"
import { MainSettingsService } from "@/services/MainSettingsService.js"
import { SmsBodiesService } from "@/services/SmsBodiesService.js"
import { SmsResendInformationKeyService } from "@/services/SmsResendInformationKeyService.js"
import { SmsVariableDefaultValueService } from "@/services/SmsVariableDefaultValueService.js"
import { EventBus } from "@/shared/eventbus.js"
import Widget from "@/shared/Widget.vue"

export default {
  name: "SmsSettings",
  mixins: [notify],
  components: { SmsApplianceRemindRate, SmsBody, Widget },
  props: {
    smsBodies: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      tab: "confirmation",
      progress: false,
      smsBodiesService: new SmsBodiesService(),
      smsResendInformationKeyService: new SmsResendInformationKeyService(),
      smsVariableDefaultValueService: new SmsVariableDefaultValueService(),
      mainSettingsService: new MainSettingsService(),
      transactionSmsEnabled: true,
      isValid: false,
    }
  },
  created() {
    this.getSmsVariableDefaultValues()
    this.loadTransactionSmsEnabled()
  },
  mounted() {
    this.getSmsBodies()
    this.getSmsResendInformationKey()
  },
  methods: {
    async loadTransactionSmsEnabled() {
      const mainSettings = this.$store.getters["settings/getMainSettings"]
      this.transactionSmsEnabled =
        mainSettings.transactionSmsEnabled !== undefined
          ? mainSettings.transactionSmsEnabled
          : true
    },
    async updateTransactionSmsEnabled() {
      try {
        const mainSettings = this.$store.getters["settings/getMainSettings"]
        this.mainSettingsService.mainSettings = { ...mainSettings }
        this.mainSettingsService.mainSettings.transactionSmsEnabled =
          this.transactionSmsEnabled
        await this.mainSettingsService.update()
        await this.$store.dispatch("settings/setMainSettings")
        this.alertNotify("success", "Updated Successfully")
      } catch (e) {
        this.alertNotify("error", e.message)
      }
    },
    async getSmsVariableDefaultValues() {
      try {
        await this.smsVariableDefaultValueService.getSmsVariableDefaultValues()
      } catch (e) {
        this.alertNotify("error", e.message)
      }
    },
    async getSmsBodies() {
      try {
        await this.smsBodiesService.getSmsBodies()
      } catch (e) {
        this.alertNotify("error", e.message)
      }
    },
    async getSmsResendInformationKey() {
      try {
        await this.smsResendInformationKeyService.getResendInformationKeys()
      } catch (e) {
        this.alertNotify("error", e.message)
      }
    },
    async updateSmsBodies() {
      if (this.tab === "confirmation") {
        let refs = this.$refs.smsBody_confirmation_ref
        await this.validateSmsBodies(refs)
        if (
          !this.smsBodiesService.confirmationList.filter((x) => !x.validation)
            .length
        ) {
          try {
            await this.smsBodiesService.updateSmsBodies(this.tab)
            this.alertNotify("success", "Updated Successfully")
            EventBus.$emit("Settings")
          } catch (e) {
            this.alertNotify("error", e.message)
          }
        }
      } else if (this.tab === "reminder") {
        let refs = this.$refs.smsBody_reminder_ref
        await this.validateSmsBodies(refs)
        if (
          !this.smsBodiesService.reminderList.filter((x) => !x.validation)
            .length
        ) {
          try {
            await this.smsBodiesService.updateSmsBodies(this.tab)
            this.alertNotify("success", "Updated Successfully")
            EventBus.$emit("Settings")
          } catch (e) {
            this.alertNotify("error", e.message)
          }
        }
      } else {
        let refs = this.$refs.smsBody_resend_ref
        await this.validateSmsBodies(refs)
        if (
          !this.smsBodiesService.resendInformationList.filter(
            (x) => !x.validation,
          ).length
        ) {
          try {
            await this.smsResendInformationKeyService.updateResendInformationKey()
            await this.smsBodiesService.updateSmsBodies(this.tab)
            this.alertNotify("success", "Updated Successfully")
            EventBus.$emit("Settings")
          } catch (e) {
            this.alertNotify("error", e.message)
          }
        }
      }
    },
    async validateSmsBodies(refs) {
      for (const ref of refs) {
        await ref.validateBody()
      }
    },
  },
}
</script>

<style scoped lang="scss">
.reminder-templates {
  margin-top: 1.5rem;
}
.notice-message-area {
  padding: 20px;
  background-color: #badee4;
  margin: 10px;
  d-webkit-border-radius: 16px;
  -moz-border-radius: 16px;
  border-radius: 16px;
}
.transaction-sms-toggle {
  padding: 16px 20px;
  margin-bottom: 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}
.toggle-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.toggle-label {
  font-size: 1rem;
  font-weight: 500;
}
.toggle-description {
  font-size: 0.85rem;
  color: #666;
  margin: 4px 0 0 0;
}

@media only screen and (max-width: 767px) {
  ::v-deep .md-tabs-content {
    height: auto !important;
  }

  ::v-deep .md-tabs-navigation {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  ::v-deep .md-tab-nav-button {
    min-width: auto;
    flex-shrink: 0;
    font-size: 12px;
    padding: 0 8px;
  }
}
</style>
